import React, { useState, useEffect } from "react";
import { MenuItem, ListItemIcon, Stack, Tooltip, IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Page from "components/Page/Page";
import Table, { HeadCell } from "components/Table";
import { LooserChallenge } from "models/SoireeDefis/LooserChallenge";
import { SDLooserChallengeService } from "services/api/SoireeDefis/SDLooserChallengeService";
import { useFeedback } from "components/Provider/Feedback/FeedbackProvider";
import IconMenu from "components/IconMenu";
import { useDisclose } from "hooks/discloseHook";
import LooserChallengeFormDialog from "./LooserChallengeFormDialog";
import { Utils } from "utils/Utils";

function SDLooserChallengePage () {
	const [challenges, setChallenges] = useState<LooserChallenge[]>([]);
	const [selectedChlges, setSelectedChlges] = useState<string[]>([]);
	const [isChlgeFormOpen, openChlgeForm, closeChlgeForm] = useDisclose(false);
	const [chlgeToEdit, setChlgeToEdit] = useState<LooserChallenge | undefined>();

	const { openAlert, openConfirmDialog } = useFeedback();

	useEffect(() => {
		fetchChallenges();
	}, []);

	const fetchChallenges = async () => {
		try {
			const result = await SDLooserChallengeService.getAll();

			if (result.isError) {
				throw result.message;
			}

			setChallenges(result.value.reverse());

		} catch (error) {
			openAlert("error", error as string);
		}
	};

	const handleSubmitNewChallenge = async (challenge: LooserChallenge) => {
		try {
			const result = await SDLooserChallengeService.create(challenge.description, challenge.levelName);
			if (result.isError) {
				throw result.message;
			}

			setChallenges(challenges => [result.value, ...challenges]);
			closeChlgeForm();
			openAlert("success", "Le gage a été créé avec succès !");

		} catch (error: unknown) {
			openAlert("error", error as string);
		}
	};

	const handleOpenEditForm = (challenge: LooserChallenge) => {
		setChlgeToEdit(challenge);
		openChlgeForm();
	};

	const handleEditChallenge = async (challenge: LooserChallenge) => {
		try {
			const chlgeInx = challenges.findIndex(c => c._id === chlgeToEdit?._id);

			if (!chlgeToEdit || chlgeInx === -1) {
				throw "Une erreur est survenue !";
			}

			const { description, levelName } = challenge;

			const result = await SDLooserChallengeService.update(chlgeToEdit._id, description, levelName);
			if (result.isError) {
				throw result.message;
			}

			setChallenges(challenges => {
				const copyChallenges = [...challenges];
				copyChallenges[chlgeInx] = result.value;

				return copyChallenges;
			});

			closeChlgeForm();
			openAlert("success", "Le gage a été mis à jour !");

		} catch (error: unknown) {
			openAlert("error", error as string);
		}
	};

	const handleDeleteChallenge = (challengeId: string) => {
		const doDelete = async () => {
			try {
				const result = await SDLooserChallengeService.delete(challengeId);
				if (result.isError) {
					throw result.message;
				}

				setChallenges(challenges => challenges.filter(c => c._id !== challengeId));
				openAlert("success", "Le gage a été supprimé avec succès !");

			} catch (error: unknown) {
				openAlert("error", error as string);
			}
		};

		openConfirmDialog({
			title: "Supprimer un gage",
			msg: "Êtes-vous sûr de vouloir supprimer ce gage ?",
			onConfirm: doDelete
		});
	};

	const handleExportChallenges = () => {
		const chlgesToExport = challenges.filter(c => selectedChlges.includes(c._id));
		Utils.exportData(JSON.stringify(chlgesToExport), "LooserChallenges.json");
	};

	const tableColumns: HeadCell[] = [
		{
			id: "description",
			label: "Description",
		},
		{
			id: "levelName",
			label: "Catégorie",
		},
		{
			id: "action-menu",
			label: "",
			render: (row: LooserChallenge) => (
				<IconMenu
					id={`challenge-item-menu-${row._id}`}
					icon={<MoreVertIcon />}
				>
					<MenuItem divider onClick={() => handleOpenEditForm(row)}>
						<ListItemIcon>
							<EditIcon />
						</ListItemIcon>
						Modifier
					</MenuItem>
					<MenuItem onClick={() => handleDeleteChallenge(row._id)}>
						<ListItemIcon>
							<DeleteIcon />
						</ListItemIcon>
						Supprimer
					</MenuItem>
				</IconMenu>
			),
		}
	];

	const getToolActions = () => {
		const handleOpenChlgeForm  = () => {
			setChlgeToEdit(undefined);
			openChlgeForm();
		};

		return (
			<Stack direction="row" spacing={2}>
				<Tooltip title="Nouveau gage">
					<IconButton onClick={handleOpenChlgeForm}>
						<AddIcon />
					</IconButton>
				</Tooltip>
			</Stack>
		);
	};

	const getItemGroupActions = () => {
		return (
			<Stack direction="row" spacing={2}>
				<Tooltip title="Export au format json">
					<IconButton onClick={handleExportChallenges}>
						<FileDownloadIcon />
					</IconButton>
				</Tooltip>
			</Stack>
		);
	};

	return (
		<Page>
			<Table
				title="Les gages"
				columns={tableColumns}
				data={challenges}
				rowIdentifier={(row: LooserChallenge) => row._id}
				toolAction={getToolActions()}
				itemGroupAction={getItemGroupActions()}
				onSelectedChanged={setSelectedChlges}
			/>
			<LooserChallengeFormDialog
				isOpen={isChlgeFormOpen}
				initialValue={chlgeToEdit}
				onClose={closeChlgeForm}
				onSubmit={chlgeToEdit ? handleEditChallenge : handleSubmitNewChallenge}
			/>
		</Page>
	);
}

export default SDLooserChallengePage;